import { useEffect, useState } from "preact/hooks";
import { getApiFailureMessage } from "../../lib/gboApiClient";
import {
  useLazyGetPortalUserQuery,
  useLazyGetPortalAccountsQuery,
  useSelectPortalAccountMutation,
  useStartPortalVerificationMutation,
  useResendPortalVerificationMutation,
  useSignInPortalMutation,
  useAddPortalVerifiedEmailMutation,
  useLazyGetOnboardingDetailsQuery,
  type OnboardingDetails,
  type OnboardingContext,
  type InvoiceProofContext,
  type VerificationChallenge,
} from "../../store/portalAuthApi";

type OnboardingStep = (
  | { step: "initializing" }
  | { step: "invoiceRecovery" }
  | { step: "enterEmail"; email: string }
  | { step: "selectEmail"; emails: string[] }
  | { step: "verify"; email: string; challenge: VerificationChallenge }
) & { error?: string };

type Props = {
  onComplete: () => void;
  accessToken?: string;
  onAccessTokenRejected?: () => void;
  skipInitialization?: boolean;
};

export function useOnboardingFlow({
  onComplete,
  accessToken,
  onAccessTokenRejected,
  skipInitialization = false,
}: Props) {
  const [startVerification, startVerificationResult] = useStartPortalVerificationMutation();
  const [resendVerification, resendResult] = useResendPortalVerificationMutation();
  const [getOnboardingDetails, onboardingDetailsResult] = useLazyGetOnboardingDetailsQuery();
  const [signIn, signInResult] = useSignInPortalMutation();
  const [addEmail, addEmailResult] = useAddPortalVerifiedEmailMutation();
  const [getUser, userResult] = useLazyGetPortalUserQuery();
  const [getAccounts, accountsResult] = useLazyGetPortalAccountsQuery();
  const [selectAccount, selectAccountResult] = useSelectPortalAccountMutation();

  const [step, setStep] = useState<OnboardingStep>(
    skipInitialization ? { step: "enterEmail", email: "" } : { step: "initializing" },
  );
  const [context, setContext] = useState<OnboardingContext | undefined>(
    accessToken ? { kind: "accessToken", token: accessToken } : undefined,
  );
  const [onboardingDetails, setOnboardingDetails] = useState<OnboardingDetails>();

  async function initializeOnboarding() {
    try {
      if (context) {
        const onboardingDetails = await getOnboardingDetails(context);
        if (onboardingDetails.data) {
          await onboardCustomer(context, onboardingDetails.data);
          return;
        }
        if (!hasErrorStatus(onboardingDetails.error, 400)) {
          setStep({
            step: "initializing",
            error: getApiFailureMessage(onboardingDetails.error, "Something went wrong."),
          });
          return;
        }
        setContext(undefined);
        if (onAccessTokenRejected) {
          onAccessTokenRejected();
          return;
        }
      }
      // Sanity check they're not already signed in. This will also restore the access token in the
      // case where local storage was cleared but the refresh token is still present in the cookie
      const session = await getUser();
      if (session.data) {
        onComplete();
        return;
      }
      if (!hasErrorStatus(session.error, 401)) {
        setStep({
          step: "initializing",
          error: getApiFailureMessage(session.error, "Something went wrong."),
        });
        return;
      }
      setStep({ step: "enterEmail", email: "" });
    } catch (error) {
      setStep({
        step: "initializing",
        error: getApiFailureMessage(error, "Something went wrong."),
      });
    }
  }

  async function onboardCustomer(
    customerContext: OnboardingContext,
    onboardingDetails: OnboardingDetails,
  ) {
    const accounts = await getAccounts();
    if (accounts.error && !hasErrorStatus(accounts.error, 401)) throw accounts.error;

    // If the current auth already includes the account tied to the context/token, we can just set it here
    if (accounts.data?.some((account) => account.customerId === onboardingDetails.customerId)) {
      await selectAccount({ customerId: onboardingDetails.customerId }).unwrap();
      onComplete();
      return;
    }

    setContext(customerContext);
    setOnboardingDetails(onboardingDetails);

    const verificationStep: OnboardingStep = onboardingDetails.emails.length
      ? { step: "selectEmail", emails: onboardingDetails.emails }
      : { step: "enterEmail", email: "" };

    // Fresh magic links don't require additional verification, sign in with the token here
    if (!onboardingDetails.requiresVerification) {
      try {
        await signIn({ context: customerContext }).unwrap();
        onComplete();
      } catch (error) {
        setStep({
          ...verificationStep,
          error: getApiFailureMessage(error, "Something went wrong."),
        });
      }
    } else {
      setStep(verificationStep);
    }
  }

  useEffect(() => {
    if (step.step === "initializing") void initializeOnboarding();
  }, [step.step]);

  function changeEmail() {
    if (onboardingDetails?.emails.length) {
      setStep({ step: "selectEmail", emails: onboardingDetails.emails });
    } else {
      setStep({ step: "enterEmail", email: step.step === "verify" ? step.email : "" });
    }
  }

  function showInvoiceRecovery() {
    setStep({ step: "invoiceRecovery" });
  }

  async function submitInvoiceProof(proof: InvoiceProofContext) {
    if (step.step !== "invoiceRecovery") return;
    setStep({ step: "invoiceRecovery" });
    try {
      const details = await getOnboardingDetails(proof).unwrap();
      await onboardCustomer(proof, details);
    } catch (error) {
      setStep({
        step: "invoiceRecovery",
        error: getApiFailureMessage(error, "Something went wrong."),
      });
    }
  }

  async function start(email: string) {
    if (step.step !== "enterEmail" && step.step !== "selectEmail") return;
    email = email.trim();
    setStep({ ...step, error: undefined });
    try {
      const challenge = await startVerification({ email, context }).unwrap();
      setStep({ step: "verify", email, challenge });
    } catch (error) {
      setStep({
        ...step,
        error: getApiFailureMessage(error, "Something went wrong."),
      });
    }
  }

  async function resend() {
    if (step.step !== "verify") return;
    setStep({ ...step, error: undefined });
    try {
      const challenge = await resendVerification({
        challengeId: step.challenge.challengeId,
      }).unwrap();
      setStep({ ...step, challenge, error: undefined });
    } catch (error) {
      setStep({
        ...step,
        error: getApiFailureMessage(error, "Something went wrong."),
      });
    }
  }

  async function signInWithCode(code: string) {
    if (step.step !== "verify") return;
    setStep({ ...step, error: undefined });
    try {
      await signIn({
        challengeId: step.challenge.challengeId,
        code: code.trim(),
        context,
      }).unwrap();
    } catch (error) {
      setStep({
        ...step,
        error: getApiFailureMessage(error, "Something went wrong."),
      });
      return;
    }
    onComplete();
  }

  async function linkEmailWithCode(code: string) {
    if (step.step !== "verify") return;
    setStep({ ...step, error: undefined });
    try {
      await addEmail({
        challengeId: step.challenge.challengeId,
        code: code.trim(),
      }).unwrap();
    } catch (error) {
      setStep({
        ...step,
        error: getApiFailureMessage(error, "Something went wrong."),
      });
      return;
    }
    onComplete();
  }

  const pending =
    (step.step === "initializing" && !step.error) ||
    userResult.isFetching ||
    accountsResult.isFetching ||
    selectAccountResult.isLoading ||
    onboardingDetailsResult.isFetching ||
    startVerificationResult.isLoading ||
    resendResult.isLoading ||
    signInResult.isLoading ||
    addEmailResult.isLoading;

  const maskVerificationEmail = !!onboardingDetails?.emails.length;
  const isEnrollingEmail = !!context && onboardingDetails?.emails.length === 0;

  return {
    ...step,
    context,
    onboardingDetails,
    pending,
    maskVerificationEmail,
    isEnrollingEmail,
    changeEmail,
    showInvoiceRecovery,
    submitInvoiceProof,
    start,
    resend,
    signInWithCode,
    linkEmailWithCode,
  };
}

type OnboardingFlowState = ReturnType<typeof useOnboardingFlow>;
export type EmailEntryFlow = Extract<OnboardingFlowState, { step: "enterEmail" }>;
export type EmailSelectionFlow = Extract<OnboardingFlowState, { step: "selectEmail" }>;
export type CodeVerificationFlow = Extract<OnboardingFlowState, { step: "verify" }>;
export type InvoiceRecoveryFlow = Extract<OnboardingFlowState, { step: "invoiceRecovery" }>;

function hasErrorStatus(error: unknown, ...statuses: number[]): boolean {
  return (
    !!error &&
    typeof error === "object" &&
    "status" in error &&
    typeof error.status === "number" &&
    statuses.includes(error.status)
  );
}
